import { __awaiter } from "tslib";
import { FacadeRegistry } from './facade-registry';
import { JWebWeb } from './web-runtime';
const getGlobalThis = () => {
    if (typeof window !== 'undefined') {
        return window;
    }
    else if (typeof self !== 'undefined') {
        return self;
    }
    else if (typeof global !== 'undefined') {
        return global;
    }
    return {};
};
export const getGlobalJWeb = () => getGlobalThis().JWeb;
export const isGlobalJWebSet = () => getGlobalThis().JWeb !== undefined;
const setGlobalJWeb = (globalJWeb) => getGlobalThis().JWeb = globalJWeb;
export const getGlobalJWebRegistry = () => getGlobalThis().JWebRegistry;
export const isGlobalJWebRegistrySet = () => getGlobalThis().JWebRegistry !== undefined;
const setGlobalJWebRegistry = (globalJWebRegistry) => getGlobalThis().JWebRegistry = globalJWebRegistry;
// Remove this legacy event type once it is no longer a backwards compatibility concern
const JWEB_READY_EVENT_TYPE_LEGACY = 'jWebReady';
const JWEB_READY_EVENT_TYPE = 'com.hp.jarvis.jweb.event.ready';
const addJWebReadyEventListener = (listener) => {
    var _a, _b;
    console.log('global::addJWebReadyEventListener');
    const globalThis = getGlobalThis();
    (_a = globalThis.addEventListener) === null || _a === void 0 ? void 0 : _a.call(globalThis, JWEB_READY_EVENT_TYPE_LEGACY, listener);
    (_b = globalThis.addEventListener) === null || _b === void 0 ? void 0 : _b.call(globalThis, JWEB_READY_EVENT_TYPE, listener);
};
const removeJWebReadyEventListener = (listener) => {
    var _a, _b;
    console.log('global::removeJWebReadyEventListener');
    const globalThis = getGlobalThis();
    (_a = globalThis.removeEventListener) === null || _a === void 0 ? void 0 : _a.call(globalThis, JWEB_READY_EVENT_TYPE_LEGACY, listener);
    (_b = globalThis.removeEventListener) === null || _b === void 0 ? void 0 : _b.call(globalThis, JWEB_READY_EVENT_TYPE, listener);
};
// The bridge timeout triggers too early roughly 50% of the time on older versions of Android
// (check on an Android 24 emulator), so we need to retry it a few times to make it safer. The
// bridge timeout duration is set to zero so that the timeout function is simply pushed to the
// back of the execution stack. The bridge timeout duration is incremented slightly each retry
// so that subsequent timeouts allow for a bigger bridge injection window.
const BRIDGE_TIMEOUT_RETRIES = 2;
const BRIDGE_TIMEOUT_DURATION = 0;
const BRIDGE_TIMEOUT_INCREMENT = 25;
let timeout;
const setBridgeTimeout = (callback, retries = BRIDGE_TIMEOUT_RETRIES, duration = BRIDGE_TIMEOUT_DURATION, increment = BRIDGE_TIMEOUT_INCREMENT) => {
    console.log(`global::setBridgeTimeout: called with retries=${retries} duration=${duration} increment=${increment}`);
    const timeoutCallback = () => {
        if (isGlobalJWebSet()) {
            console.log('global::setBridgeTimeout: found global JWeb');
            callback();
        }
        else if (retries) {
            console.log('global::setBridgeTimeout: bridge timed out, will retry');
            setBridgeTimeout(callback, retries - 1, duration + increment, increment);
        }
        else {
            console.log('global::setBridgeTimeout: bridge timed out');
            callback();
        }
    };
    timeout = setTimeout(timeoutCallback, duration);
};
const clearBridgeTimeout = () => {
    console.log('global::clearBridgeTimeout');
    clearTimeout(timeout);
    timeout = null;
};
const resolveJWeb = (resolve) => __awaiter(void 0, void 0, void 0, function* () {
    console.log('global::resolveJWeb');
    let globalJWeb = getGlobalJWeb();
    if (!globalJWeb) {
        console.log('global::resolveJWeb: did not find global JWeb, will create web platform JWeb');
        globalJWeb = new JWebWeb();
        pluginsToRegister.forEach((pluginWrapper) => {
            registerPlugin(pluginWrapper.name, pluginWrapper.plugin, globalJWeb);
        });
        pluginsToRegister = [];
    }
    yield FacadeRegistry.adaptJWeb(globalJWeb);
    if (!isGlobalJWebSet()) {
        console.log('global::resolveJWeb: did not find global JWeb, will set global JWeb');
        setGlobalJWeb(globalJWeb);
    }
    console.log('global::resolveJWeb: ready!');
    resolve(globalJWeb);
});
const getJWebReadyPromise = () => new Promise((resolve) => {
    console.log('global::jWebReady');
    if (isGlobalJWebSet()) {
        console.log('global::jWebReady: found global JWeb');
        resolveJWeb(resolve);
    }
    else {
        console.log('global::jWebReady: did not find global JWeb, will set bridge timeout and event listener');
        const resolveJWebCallback = (event) => {
            console.log(`global::jWebReady::resolveJWebCallback: called with event.type=${event === null || event === void 0 ? void 0 : event.type}`);
            removeJWebReadyEventListener(resolveJWebCallback);
            clearBridgeTimeout();
            resolveJWeb(resolve);
        };
        addJWebReadyEventListener(resolveJWebCallback);
        setBridgeTimeout(resolveJWebCallback);
    }
});
let pluginsToRegister = [];
const registerPlugin = (name, plugin, globalJWeb = getGlobalJWeb()) => {
    console.log(`global::registerPlugin: called with name=${name}`);
    if (globalJWeb) {
        if (globalJWeb.platform !== 'web') {
            console.warn(`global::registerPlugin: did not register plugin ${name} in JWeb (platform is not 'web')`);
        }
        else if (!globalJWeb.Plugins || !globalJWeb.Plugins[name]) {
            console.error(`global::registerPlugin: did not register plugin ${name} in JWeb (missing 'Plugins' attribute in JWeb)`);
        }
        else if (globalJWeb.Plugins[name] === plugin) {
            console.log(`global::registerPlugin: did not register plugin ${name} in JWeb (the provided ${name} instance is already registered in JWeb)`);
        }
        else if (Object.keys(globalJWeb.Plugins[name]).length !== 0) {
            console.warn(`global::registerPlugin: did not register plugin ${name} in JWeb (a different ${name} instance is already registered in JWeb)`);
        }
        else {
            console.log(`global::registerPlugin: registered plugin ${name} in JWeb`);
            globalJWeb.Plugins[name] = plugin;
        }
    }
    else {
        const pluginToRegister = pluginsToRegister.find((pluginWrapper) => pluginWrapper.name === name);
        if ((pluginToRegister === null || pluginToRegister === void 0 ? void 0 : pluginToRegister.plugin) === plugin) {
            console.log(`global::registerPlugin: did not enqueue plugin ${name} for registration in JWeb (the provided ${name} instance is already enqueued for registration in JWeb)`);
        }
        else if (pluginToRegister) {
            console.warn(`global::registerPlugin: did not enqueue plugin ${name} for registration in JWeb (a different ${name} instance is already enqueued for registration in JWeb)`);
        }
        else {
            console.log(`global::registerPlugin: enqueued plugin ${name} for registration in JWeb`);
            pluginsToRegister.push({ plugin, name });
        }
    }
};
const initializeGlobal = () => {
    console.log('global::initializeGlobal');
    if (!isGlobalJWebRegistrySet()) {
        console.log('global::initializeGlobal: did not find global JWebRegistry, will set global JWebRegistry');
        setGlobalJWebRegistry({ jWebReady: getJWebReadyPromise(), registerPlugin });
    }
};
initializeGlobal();
//# sourceMappingURL=global.js.map